import React from 'react';

import { useQuery } from '@tanstack/react-query';

import Table from '../../../../Table';
import FormContainer from '../../../FormContainer';

import useUsersForm from '../../../../../forms/lender/users';
import UserService from '../../../../../services/UserService';
import { useUiContext } from '../../../../../context/UiContext';
import { useServicerStore } from '../../../../../store';
import LenderService from '../../../../../services/LenderService';

export default function Users({ lender, isFetching, refetchLender }) {
  const { showToast, showDialogModal } = useUiContext();

  const userRole = useServicerStore((state) => state.userRole);
  const isSuperAdmin = userRole === 'superAdmin';
  const isLender = userRole === 'lender';

  const { data: users, isFetching: areUsersFetching } = useQuery({
    queryKey: ['lenderUsers', lender?.id],
    queryFn: () => LenderService.getLenderUsers(lender?.id),
    enabled: !!lender?.id && isLender,
  });

  const cols = [
    {
      title: 'Name',
      keys: ['name'],
    },
    {
      title: 'Email',
      keys: ['email'],
    },
    {
      title: 'Last login',
      keys: ['last_login'],
      format: (value) => {
        if (!value) {
          return 'n/a';
        }
        return new Date(+value).toLocaleDateString();
      },
    },
    {
      title: 'Actions',
      type: 'delete',
      onClick: async (row) => {
        showDialogModal({
          title: 'Are you sure want to remove this user from lender access?',
          message:
            "Note: This action will not remove the user's account, it will only remove the user's access to this lender.",
          onConfirm: async () => {
            try {
              await UserService.revokeUser({
                userId: row.id,
                lenderId: lender.id,
              });
              refetchLender();
              showToast({
                text: 'User revoked successfully',
                type: 'success',
              });
            } catch (error) {
              console.log(error);
              showToast({
                text: error?.response?.data?.message || 'Something went wrong',
                type: 'error',
              });
            }
          },
        });
      },
    },
  ];

  const currentCols = isSuperAdmin
    ? cols
    : cols.filter(
        (col) => col.title !== 'Actions' && col.title !== 'Last login'
      );

  const inviteUser = async (formValues, { resetForm }) => {
    try {
      await UserService.inviteUser({ formValues, lenderId: lender.id });
      refetchLender();
      resetForm();
      showToast({
        text: 'User invited successfully',
        type: 'success',
      });
    } catch (error) {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    }
  };

  const usersForm = useUsersForm(inviteUser);

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <div className="mb-10">
        <Table
          cols={currentCols}
          rows={isLender ? users : lender?._users}
          title="Users"
          isFetching={isLender ? areUsersFetching : isFetching}
          minHeight={200}
          loaderTop={84}
        />
      </div>
      {isSuperAdmin && <FormContainer form={usersForm} noBillComId />}
    </div>
  );
}
